'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { RefreshCw, TrendingUp, TrendingDown, Activity, Search, ArrowUpDown, ArrowUp, ArrowDown, BarChart2 } from 'lucide-react'
import WeeklyMoversSocialCard from '@/components/WeeklyMoversSocialCard'

// ─── Types ────────────────────────────────────────────────────────────────────

interface MoverRow {
  symbol:       string
  sec_type:     string | null
  price_start:  number
  price_now:    number
  price_change: number
  pct_change:   number
  alert_count:  number
  max_rvol:     number
}

interface MoverData {
  stocks:      MoverRow[]
  count:       number
  generatedAt: string
}

type Filter     = 'all' | 'gainers' | 'losers'
type SortCol    = 'pct_change' | 'price_change' | 'price_now' | 'alert_count' | 'max_rvol' | 'symbol'
type SortDir    = 'asc' | 'desc'

interface DailyCandleRow {
  symbol: string
  avg_candle_pct: number
  avg_abs_pct: number
  trading_days: number
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Map raw Polygon security type codes to human-readable labels.
// CS and OS (ordinary shares) need no label — they're standard equities.
const SEC_TYPE_LABEL: Record<string, string> = {
  ETF:     'ETF',
  ADRC:    'ADR',
  PFD:     'Pref',
  WARRANT: 'Warr',
  SP:      'Bond',
  ETS:     'ETS',
  ETV:     'ETV',
  ETN:     'ETN',
  FUND:    'Fund',
  UNIT:    'Unit',
  RIGHT:   'Right',
  GDR:     'GDR',
  NYRS:    'NYRS',
}

function secTypeLabel(type: string | null): string | null {
  if (!type || type === 'CS' || type === 'OS') return null
  return SEC_TYPE_LABEL[type] ?? type
}

function fmt$(n: number, digits = 2) {
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits })
}

function fmtPct(n: number) {
  if (!Number.isFinite(n)) return '—'
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`
}

function fmtTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' })
  } catch { return '' }
}

function pctClass(n: number) {
  if (n > 0) return 'text-emerald-400'
  if (n < 0) return 'text-rose-400'
  return 'text-zinc-400'
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-zinc-800 bg-zinc-900/60 px-5 py-4">
      <span className="text-xs font-medium text-zinc-500 uppercase tracking-wide">{label}</span>
      <span className={`text-2xl font-bold tabular-nums ${color ?? 'text-zinc-100'}`}>{value}</span>
      {sub && <span className="text-xs text-zinc-500">{sub}</span>}
    </div>
  )
}

// ─── Sort Icon ────────────────────────────────────────────────────────────────

function SortIcon({ col, active, dir }: { col: SortCol; active: SortCol; dir: SortDir }) {
  if (col !== active) return <ArrowUpDown className="w-3 h-3 text-zinc-600" />
  return dir === 'desc'
    ? <ArrowDown className="w-3 h-3 text-amber-400" />
    : <ArrowUp className="w-3 h-3 text-amber-400" />
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const DAY_OPTIONS = [
  { days: 1, label: '1-Day',  short: '1D' },
  { days: 3, label: '3-Day',  short: '3D' },
  { days: 7, label: '7-Day',  short: '7D' },
]

export default function WeeklyMoversClient() {
  const searchParams = useSearchParams()

  // Read days from ?days=N, default to 7
  const daysParam = parseInt(searchParams.get('days') ?? '7', 10)
  const days = DAY_OPTIONS.find(o => o.days === daysParam)?.days ?? 7
  const dayLabel = DAY_OPTIONS.find(o => o.days === days)?.label ?? '7-Day'

  const [data,        setData]        = useState<MoverData | null>(null)
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState<string | null>(null)
  const [filter,      setFilter]      = useState<Filter>('all')
  const [search,      setSearch]      = useState('')
  const [sortCol,     setSortCol]     = useState<SortCol>('pct_change')
  const [sortDir,     setSortDir]     = useState<SortDir>('desc')
  const [lastFetch,   setLastFetch]   = useState<Date | null>(null)
  const [candleData,  setCandleData]  = useState<DailyCandleRow[]>([])
  const [candleLoad,  setCandleLoad]  = useState(true)
  const [candleDays,  setCandleDays]  = useState(0)

  // ⭐ NO AUTH GUARD, DELIBERATELY (owner decision 2026-08-15). There used to be a useEffect here
  // that router.push('/auth')ed anonymous visitors on MOUNT, while this page sat in sitemap.ts —
  // so search engines were sent to a login wall and every referred visitor bounced before seeing
  // a single mover. /api/weekly-movers was opened in the same change (it 401'd, and nothing in it
  // is user-specific), so the data loads for everyone now.
  // ⚠ If this page is ever gated again, remove it from sitemap.ts in the SAME change.
  // app/sitemapGating.test.ts fails on that pairing, so it is checked rather than remembered.

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/weekly-movers?days=${days}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      // pg driver returns numeric columns as strings to preserve precision.
      // Parse every numeric field to a JS number here so .toFixed() etc. never throw.
      const stocks: MoverRow[] = (json.stocks ?? []).map((r: Record<string, unknown>) => ({
        symbol:        String(r.symbol ?? ''),
        sec_type:      r.sec_type ? String(r.sec_type) : null,
        price_start:  parseFloat(String(r.price_start  ?? 0)),
        price_now:    parseFloat(String(r.price_now    ?? 0)),
        price_change: parseFloat(String(r.price_change ?? 0)),
        pct_change:   parseFloat(String(r.pct_change   ?? 0)),
        alert_count:   parseInt(String(r.alert_count     ?? 0), 10),
        max_rvol:      parseFloat(String(r.max_rvol      ?? 0)),
      }))
      setData({ stocks, count: stocks.length, generatedAt: json.generatedAt ?? '' })
      setLastFetch(new Date())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    setCandleLoad(true)
    fetch('/api/rankings/daily-candle')
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(json => {
        setCandleData(json.data ?? [])
        setCandleDays(json.trading_days_window ?? 0)
      })
      .catch(() => setCandleData([]))
      .finally(() => setCandleLoad(false))
  }, [])

  // ── Derived lists ────────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    if (!data) return []
    let rows = data.stocks

    // Direction filter
    if (filter === 'gainers') rows = rows.filter(r => r.pct_change > 0)
    if (filter === 'losers')  rows = rows.filter(r => r.pct_change < 0)

    // Search
    if (search.trim()) {
      const q = search.trim().toUpperCase()
      rows = rows.filter(r => r.symbol.includes(q))
    }

    // Sort
    rows = [...rows].sort((a, b) => {
      let av = a[sortCol] as number | string
      let bv = b[sortCol] as number | string
      if (sortCol === 'pct_change') {
        av = sortDir === 'desc' ? -Math.abs(a.pct_change) : Math.abs(a.pct_change)
        bv = sortDir === 'desc' ? -Math.abs(b.pct_change) : Math.abs(b.pct_change)
        return (av as number) - (bv as number)
      }
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv as string) : (bv as string).localeCompare(av)
      return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number)
    })

    return rows
  }, [data, filter, search, sortCol, sortDir])

  const stats = useMemo(() => {
    if (!data) return null
    const all     = data.stocks
    const gainers = all.filter(r => r.pct_change > 0)
    const losers  = all.filter(r => r.pct_change < 0)
    const topGain = [...all].sort((a, b) => b.pct_change - a.pct_change)[0]
    const topLoss = [...all].sort((a, b) => a.pct_change - b.pct_change)[0]
    return { total: all.length, gainers: gainers.length, losers: losers.length, topGain, topLoss }
  }, [data])

  const handleSort = (col: SortCol) => {
    if (col === sortCol) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortCol(col)
      setSortDir('desc')
    }
  }

  // ⛔ THE SESSION GATES ARE GONE, AND REMOVING THE useEffect ALONE WOULD NOT HAVE BEEN ENOUGH.
  // `if (!session?.user) return null` sat here, so an anonymous visitor who got past the redirect
  // would have been served a BLANK PAGE instead of a login wall — a worse outcome, and one that
  // typechecks perfectly. The `status === 'loading'` spinner went with it: nothing on this page
  // reads the session (it was referenced on that one line and nowhere else), so waiting for
  // NextAuth only delayed market data for every visitor and every crawler.

  // ── Render ───────────────────────────────────────────────────────────────────

  // Adapter for WeeklyMoversSocialCard, whose row shape uses explicit 7d field names. Kept as a single
  // named value so the mapping lives in one place rather than inline in JSX where it would be easy to
  // drop a field again. Ideally both sides would import one shared type; this is the minimal fix that
  // makes the card correct today without reshaping the API response.
  const socialRows = useMemo(() => (data?.stocks ?? []).map((r) => ({
    symbol: r.symbol,
    sec_type: r.sec_type,
    pct_change_7d: r.pct_change,
    price_now: r.price_now,
    price_7d_ago: r.price_start,
    change_7d: r.price_change,
    max_rvol: r.max_rvol,
    alert_count: r.alert_count,
  })), [data])

  return (
    <div className="min-h-screen text-zinc-100">
      {/* ── Header ── */}
      <div className="sticky top-0 z-20 border-b border-zinc-800 bg-zinc-950/95 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Link prefetch={false} href="/alerts" className="text-zinc-500 hover:text-zinc-200 text-sm transition">← Alerts</Link>
            <span className="text-zinc-700">|</span>
            {/* h2, not h1 (2026-08-16): the server shell in page.tsx now renders the page's real
                <h1> ("Weekly Stock Movers") above this widget, and two h1s on one document is worse
                than none. This heading was already styled text-base, so it read as a section header
                rather than the page title regardless. */}
            <h2 className="text-base font-semibold text-zinc-100 flex items-center gap-2">
              <Activity className="w-4 h-4 text-amber-400" />
              {dayLabel} Movers
            </h2>
            {/* Day selector */}
            <div className="flex items-center gap-1 rounded-lg border border-zinc-700/60 bg-zinc-900/60 p-0.5">
              {DAY_OPTIONS.map(opt => (
                <Link
                  key={opt.days}
                  href={`/weekly-movers?days=${opt.days}`}
                  className={[
                    'rounded-md px-3 py-1 text-xs font-bold tabular-nums transition-all',
                    days === opt.days
                      ? 'bg-amber-500 text-black shadow-sm'
                      : 'text-zinc-400 hover:text-zinc-200',
                  ].join(' ')}
                >
                  {opt.short}
                </Link>
              ))}
            </div>
            {lastFetch && (
              <span className="text-xs text-zinc-500">
                Updated {fmtTime(lastFetch.toISOString())} ET
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* 🚨 FIELD NAMES DIFFER BETWEEN THE TWO SHAPES, so this MUST be mapped rather than passed
                through. This page's rows use pct_change / price_start / price_change; the card expects
                pct_change_7d / price_7d_ago / change_7d. Passing raw gave the card `undefined` for all
                three, and because `undefined > 0` is false the card rendered 0 gainers, 0 losers and an
                arbitrary "top mover" (its sort compared NaN). TypeScript was reporting exactly this as
                TS2719 "two different types with this name exist"; the error was real, not noise. */}
            <WeeklyMoversSocialCard rows={socialRows} generatedAt={data?.generatedAt} />
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-800/60 px-3 py-1.5 text-xs font-medium text-zinc-300 hover:border-zinc-500 hover:text-zinc-100 transition disabled:opacity-40"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6 space-y-6">

        {/* ── Error ── */}
        {error && (
          <div className="rounded-xl border border-rose-800/60 bg-rose-950/40 px-4 py-3 text-sm text-rose-300">
            {error}
          </div>
        )}

        {/* ── Stat cards ── */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <StatCard label="Total Symbols"   value={stats.total.toString()}   sub={`with ${dayLabel.toLowerCase()} data`} />
            <StatCard label="Gainers"         value={stats.gainers.toString()}  color="text-emerald-400"
              sub={stats.total > 0 ? `${((stats.gainers / stats.total) * 100).toFixed(0)}% of universe` : undefined} />
            <StatCard label="Losers"          value={stats.losers.toString()}   color="text-rose-400"
              sub={stats.total > 0 ? `${((stats.losers / stats.total) * 100).toFixed(0)}% of universe` : undefined} />
            {stats.topGain && (
              <StatCard
                label="Biggest Gainer"
                value={fmtPct(stats.topGain.pct_change)}
                sub={stats.topGain.symbol}
                color="text-emerald-400"
              />
            )}
            {stats.topLoss && (
              <StatCard
                label="Biggest Loser"
                value={fmtPct(stats.topLoss.pct_change)}
                sub={stats.topLoss.symbol}
                color="text-rose-400"
              />
            )}
          </div>
        )}

        {/* ── Filters + search ── */}
        <div className="flex flex-wrap items-center gap-3">
          {(['all', 'gainers', 'losers'] as Filter[]).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium capitalize transition ${
                filter === f
                  ? 'bg-amber-500 text-black'
                  : 'border border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500'
              }`}
            >
              {f === 'all' ? `All (${data?.stocks.length ?? 0})` : f === 'gainers' ? `▲ Gainers (${stats?.gainers ?? 0})` : `▼ Losers (${stats?.losers ?? 0})`}
            </button>
          ))}

          <div className="relative ml-auto">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search symbol…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 rounded-lg border border-zinc-700 bg-zinc-800/60 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-amber-500/60 w-40"
            />
          </div>
        </div>

        {/* ── Table ── */}
        <div className="relative rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
          {/* Refresh overlay, shows while re-fetching existing data */}
          {loading && data && (
            <div className="absolute inset-0 z-10 bg-zinc-950/50 flex items-center justify-center rounded-xl">
              <RefreshCw className="w-5 h-5 animate-spin text-zinc-400" />
            </div>
          )}

          {loading && !data ? (
            <div className="flex items-center justify-center py-20 text-zinc-500 text-sm gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Loading {dayLabel.toLowerCase()} performance data…
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 bg-zinc-900/80">
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 w-12">#</th>
                    {([
                      { col: 'symbol'       as SortCol, label: 'Symbol'                },
                      { col: 'price_now'    as SortCol, label: 'Price'                 },
                      { col: 'price_change' as SortCol, label: `${dayLabel} Change $`  },
                      { col: 'pct_change'   as SortCol, label: `${dayLabel} Change %`  },
                      { col: 'alert_count'  as SortCol, label: 'Alerts'                },
                      { col: 'max_rvol'     as SortCol, label: 'Peak RVOL'             },
                    ] as { col: SortCol; label: string }[]).map(({ col, label }) => (
                      <th
                        key={col}
                        onClick={() => handleSort(col)}
                        className="px-4 py-3 text-left text-xs font-medium text-zinc-500 cursor-pointer select-none hover:text-zinc-300 transition whitespace-nowrap"
                      >
                        <span className="inline-flex items-center gap-1">
                          {label}
                          <SortIcon col={col} active={sortCol} dir={sortDir} />
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-16 text-center text-zinc-500 text-sm">
                        No stocks match the current filter
                      </td>
                    </tr>
                  )}
                  {filtered.map((row, idx) => {
                    const isGain = row.pct_change > 0
                    const isLoss = row.pct_change < 0
                    const absPct = Math.abs(row.pct_change)
                    const isHot  = absPct >= 30
                    const rank   = idx + 1

                    return (
                      <tr
                        key={row.symbol}
                        className={`border-b border-zinc-800/60 transition-colors hover:bg-zinc-800/30 ${
                          isHot ? 'bg-amber-500/5' : ''
                        }`}
                      >
                        {/* Rank, top 3 get gold/silver/bronze tint */}
                        <td className="px-4 py-3 tabular-nums w-12">
                          <span className={`text-xs font-bold ${
                            rank === 1 ? 'text-amber-400' :
                            rank === 2 ? 'text-zinc-300'  :
                            rank === 3 ? 'text-amber-700' :
                            'text-zinc-600'
                          }`}>
                            {rank}
                          </span>
                        </td>

                        {/* Symbol, click opens chart; HOT badge for extreme moves */}
                        <td className="px-4 py-3">
                          <Link href={`/chart/${row.symbol}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 group w-fit">
                            {isGain
                              ? <TrendingUp   className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                              : isLoss
                                ? <TrendingDown className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                                : <span className="w-3.5" />
                            }
                            <span className="font-semibold tracking-wide group-hover:text-amber-400 transition-colors">
                              <span className="text-zinc-100">{row.symbol}</span>
                              {secTypeLabel(row.sec_type) && (
                                <span className="text-zinc-500 font-normal text-[11px]">
                                  ·{secTypeLabel(row.sec_type)}
                                </span>
                              )}
                            </span>
                            {isHot && (
                              <span className="rounded px-1 py-0.5 text-[9px] font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                HOT
                              </span>
                            )}
                          </Link>
                        </td>

                        {/* Price, with start price inline */}
                        <td className="px-4 py-3 tabular-nums font-mono text-xs">
                          <div className="text-zinc-200">{fmt$(row.price_now)}</div>
                          <div className="text-[10px] text-zinc-600 mt-0.5">
                            <span className="text-zinc-700">{days === 1 ? 'Session open: ' : `${DAY_OPTIONS.find(o => o.days === days)?.short ?? days + 'd'} ago: `}</span>{fmt$(row.price_start)}
                          </div>
                        </td>

                        {/* $ change */}
                        <td className={`px-4 py-3 tabular-nums font-mono text-xs ${pctClass(row.price_change)}`}>
                          {row.price_change >= 0 ? '+' : ''}{fmt$(row.price_change)}
                        </td>

                        {/* % change, bar + value */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 rounded-full bg-zinc-800 overflow-hidden shrink-0">
                              <div
                                className={`h-full rounded-full ${isGain ? 'bg-emerald-500' : 'bg-rose-500'}`}
                                style={{ width: `${Math.min(absPct * 1.5, 100)}%` }}
                              />
                            </div>
                            <span className={`font-mono text-xs font-semibold tabular-nums ${pctClass(row.pct_change)}`}>
                              {fmtPct(row.pct_change)}
                            </span>
                          </div>
                        </td>

                        {/* Alert count */}
                        <td className="px-4 py-3 tabular-nums text-xs">
                          {row.alert_count > 0
                            ? <span className="rounded-md bg-zinc-800 px-2 py-0.5 text-zinc-300">{row.alert_count}</span>
                            : <span className="text-zinc-700">—</span>}
                        </td>

                        {/* Peak RVOL, ≥10 amber, ≥5 muted amber, else zinc */}
                        <td className="px-4 py-3 tabular-nums text-xs">
                          {row.max_rvol > 0
                            ? <span className={`font-mono ${
                                row.max_rvol >= 10 ? 'text-amber-400' :
                                row.max_rvol >= 5  ? 'text-amber-600' :
                                'text-zinc-500'
                              }`}>
                                {row.max_rvol.toFixed(1)}×
                              </span>
                            : <span className="text-zinc-700">—</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        {data && (
          <p className="text-center text-xs text-zinc-600">
            {filtered.length} of {data.count} symbols shown · Ranked by absolute {dayLabel.toLowerCase()} % change ·
            Universe: stocks with RVOL alerts in the last {days} {days === 1 ? 'day' : 'days'}
          </p>
        )}

        {/* ── Daily Avg Candle % Panel ── */}
        <div className="mt-8 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-violet-400" />
              <h2 className="text-sm font-semibold text-zinc-100">Avg Daily Candle %, Top 25 Movers</h2>
              {candleDays > 0 && (
                <span className="text-[10px] text-zinc-500 border border-zinc-700 rounded px-1.5 py-0.5">
                  {candleDays} trading days
                </span>
              )}
            </div>
            <p className="text-[10px] text-zinc-600">Ranked by abs avg open→close % · Ra long-term hold candidates</p>
          </div>

          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
            {candleLoad ? (
              <div className="flex items-center justify-center py-12 text-zinc-500 text-sm gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Computing daily candle averages…
              </div>
            ) : candleData.length === 0 ? (
              <div className="py-12 text-center text-zinc-600 text-sm">No data yet, accumulating daily bars.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800 bg-zinc-900/80">
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 w-10">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500">Symbol</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500">Avg Daily Candle %</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500">Abs Avg %</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500">Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candleData.map((row, idx) => {
                      const isPos = row.avg_candle_pct >= 0
                      const rank = idx + 1
                      return (
                        <tr key={row.symbol} className="border-b border-zinc-800/60 hover:bg-zinc-800/30 transition-colors">
                          <td className="px-4 py-2.5 tabular-nums w-10">
                            <span className={`text-xs font-bold ${
                              rank === 1 ? 'text-amber-400' : rank === 2 ? 'text-zinc-300' : rank === 3 ? 'text-amber-700' : 'text-zinc-600'
                            }`}>{rank}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <Link href={`/chart/${row.symbol}`} target="_blank" rel="noopener noreferrer"
                              className="font-semibold tracking-wide text-zinc-100 hover:text-violet-400 transition-colors">
                              {row.symbol}
                            </Link>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <div className="w-20 h-1.5 rounded-full bg-zinc-800 overflow-hidden shrink-0">
                                <div
                                  className={`h-full rounded-full ${isPos ? 'bg-emerald-500' : 'bg-rose-500'}`}
                                  style={{ width: `${Math.min(Math.abs(row.avg_candle_pct) * 2, 100)}%` }}
                                />
                              </div>
                              <span className={`font-mono text-xs font-semibold tabular-nums ${isPos ? 'text-emerald-400' : 'text-rose-400'}`}>
                                {row.avg_candle_pct >= 0 ? '+' : ''}{row.avg_candle_pct.toFixed(2)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 tabular-nums font-mono text-xs text-violet-400">
                            {row.avg_abs_pct.toFixed(2)}%
                          </td>
                          <td className="px-4 py-2.5 tabular-nums text-xs text-zinc-500">
                            {row.trading_days}d
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <p className="text-center text-[11px] text-zinc-700">
            Avg candle = mean of (close − open) / open × 100 per trading day · Ra swing entry candidates for key-level reloads
          </p>
        </div>

      </div>
    </div>
  )
}
